import { useState } from 'react';
import { toast } from 'sonner';
import { Languages, Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import { ENGLISH_COMFORT_OPTIONS } from '@/types/profile';
import type { EnglishComfortLevel } from '@/types/profile';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/components/ui/ErrorAlert';

export default function EditableEnglishLevelRow() {
  const { profile, user, refreshProfile } = useAuth();
  const current = profile?.english_comfort_level ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EnglishComfortLevel | null>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const currentOption = ENGLISH_COMFORT_OPTIONS.find((o) => o.value === current);
  const unchanged = draft === current;
  const canSave = !!draft && !unchanged && !saving;

  const startEdit = () => {
    setDraft(current);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(current);
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    if (!user?.id || !draft || !canSave) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { english_comfort_level: draft });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    await refreshProfile();
    track('profile_updated', { field: 'english_comfort_level' });
    setSaving(false);
    setEditing(false);
    toast.success('Saved');
  };

  return (
    <Card>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <Languages size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-sans text-sm text-text-muted">English comfort level</p>
          <p className="font-sans font-semibold text-base text-text-primary">
            {currentOption ? (
              <>
                <span className="mr-2">{currentOption.icon}</span>
                {currentOption.label}
              </>
            ) : (
              'Not set'
            )}
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            aria-label="Edit English comfort level"
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:bg-cream-warm transition-colors cursor-pointer"
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-5">
          <div className="space-y-3">
            {ENGLISH_COMFORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setDraft(option.value)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-colors flex items-center gap-3 cursor-pointer ${
                  draft === option.value
                    ? 'border-primary bg-primary-light'
                    : 'border-border bg-card hover:border-primary/30'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    draft === option.value ? 'border-primary' : 'border-border'
                  }`}
                >
                  {draft === option.value && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="text-xl">{option.icon}</span>
                <span className="font-sans font-medium text-text-primary">
                  {option.label}
                </span>
              </button>
            ))}
          </div>

          {error && <ErrorAlert error={error} className="mt-4" />}

          <div className="mt-4 flex gap-3">
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <div className="flex-1">
              <Button fullWidth onClick={save} disabled={!canSave} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
